import { useState } from "react";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { formatApiErrorDetail } from "../../lib/api";
import { empApi, setEmpSession } from "../../lib/employment";

// Passwordless sign-in: email → 6-digit code → session. `email` may be prefilled after registration.
export default function PortalAuth({ kind, title, prefillEmail = "", onSignedIn, onRegister }) {
  const [email, setEmail] = useState(prefillEmail);
  const [code, setCode] = useState("");
  const [stage, setStage] = useState(prefillEmail ? "code" : "email");
  const [busy, setBusy] = useState(false);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  const request = async (e) => {
    e?.preventDefault(); setBusy(true);
    try { await empApi.post("/auth/request-code", { email, kind }); setStage("code"); toast.success("If an account exists for this email, a 6-digit code has been sent."); }
    catch (e2) { err(e2); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await empApi.post("/auth/verify", { email, kind, code: code.trim() }); setEmpSession(data.token, kind); onSignedIn(data.profile); }
    catch (e2) { err(e2); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={stage === "email" ? request : verify} className="max-w-lg bg-[#f8f4e8] rounded-2xl p-7 border border-[#d6aa43]/20 space-y-4" data-testid={`emp-auth-${kind}`}>
      <h2 className="font-serif text-2xl font-bold">{title}</h2>
      <p className="text-sm text-[#0a0d12]/70">No password. We email you a one-time 6-digit code each time you sign in.</p>
      <label className="block"><span className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Email address</span>
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={stage === "code"} className="w-full px-4 py-3 border border-black/15 rounded-md bg-white" data-testid="emp-auth-email" /></label>
      {stage === "code" && (
        <label className="block"><span className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">6-digit code from your email</span>
          <input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="w-full px-4 py-3 border border-black/15 rounded-md bg-white font-mono text-xl tracking-[0.4em]" data-testid="emp-auth-code" autoFocus /></label>
      )}
      <button disabled={busy} className="dk-btn-primary w-full justify-center" data-testid="emp-auth-submit">{busy ? "Please wait…" : stage === "email" ? <><MailCheck size={16} /> Email me a code</> : "Sign in"}</button>
      {stage === "code" && <button type="button" onClick={request} disabled={busy} className="text-xs underline text-[#0d1724]/70" data-testid="emp-auth-resend">Send a new code</button>}
      {onRegister && <p className="text-xs text-[#0a0d12]/60">New here? <button type="button" onClick={onRegister} className="underline font-semibold" data-testid="emp-auth-register">Create your {kind === "partner" ? "Job Partner" : "Job Seeker"} account</button></p>}
    </form>
  );
}
